import {
  ButtonPrimaryCompact,
  CardContent,
  colors2,
  ButtonGhostV2,
  Container,
  DropdownSingle,
  LoaderOverlay,
  PaddingBottom20,
  PaddingTop40,
  Section,
  TabBarV2,
  UI_BODY_SEMIBOLD_SM,
  UI_TITLE_SM,
  Wrapper700,
} from "oolib";
import { useLogout } from "../../utils/react-query-hooks/auth";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useLocation } from "react-router-dom";
import { HeaderLogo } from "../../components/nav/Header/PrimaryHeaderV2/HeaderUI/comps/HeaderLogo";
import { useBeginPublishingFlow } from "../../utils/contributionFlowUtils";
import { useSearchParamsState } from "../../utils/customHooks";
import {
  __GetContentTypeConfigNew,
  __GetProfileTypeConfig,
} from "../../utils/getters/gettersV2";
import { useGetQueryData } from "../../utils/react-query-hooks/general";
import ContributeTpl from "../ContributeTpl";
import SM_PublishedPage from "../../_self_managed/SM_PublishedPage";
import { useQuickLinkParser } from "./useQuickLinkParser";
import MultiSubmissionTab from "./MultiSubmissionTab";

/** this sort of config needed for the requird profileType, to activate this module
      "selfServeSurveyConfig": {
        "surveys": [
          {
            "contentType": "expressionOfInterest",
            "initActive": true,
            "enableTriggerNextSurvey": true
          },
          {
            "contentType": "surveyQuestionnaires",
            "initActive": false
          },
          {
            "contentType": "weeklyCheckIn",
            "initActive": true,
            "allowMultipleSubmissions": true
          }
        ]
      }

  SINGLE vs MULTI submission:
  - default (no `allowMultipleSubmissions`): one submission per content type, tracked
    on `user.selfServeSurveys`. Rendered together in the "Surveys" card list, exactly
    as it always has been.
  - `allowMultipleSubmissions: true`: the content type gets its OWN tab, listing the
    user's own docs (via meta.kp_contributed_by) with a "+ New submission" button.
    No `user.selfServeSurveys` bookkeeping at all (see MultiSubmissionTab).

  Tabs only render when there is more than one tab. A pure single-submission tenant
  (i.e. no multi configs) renders identically to before — no tab bar.
 */

const SURVEYS_TAB = "__surveys__";

// sentinel surveyData value: renders like `null` (so ContributeTpl/SM_PublishedPage
// unmount), but ALSO triggers the post-unmount cleanup effect. We need this for
// self-serve publish: useSubmitPublishContent intentionally does NOT remove the
// ["Contributions","edit",…] query (doing so while ContributeTpl is still mounted
// would refetch getContentForEdit on the just-published doc → spurious editPublished
// clone). So we remove it here, in an effect that runs AFTER React has committed the
// unmount — at which point there's provably no observer left to refetch.
const UNMOUNT_CONTRIBUTE_TPL = "-unmount-contribute-tpl-";

const SelfServeSurvey = () => {
  const location = useLocation();
  const userData = useGetQueryData("userData");
  const profileConfig = __GetProfileTypeConfig(userData?.user?.profileType);
  const originalFrom = useRef(location.state?.from);
  const queryClient = useQueryClient();
  const { generateFn, generateTplIsLoading } = useBeginPublishingFlow();
  const { logout } = useLogout();

  const [surveyData, setSurveyData] = useSearchParamsState({
    key: "survey",
    value: null, // will contain { contentType, documentId, mode: 'edit' | 'view' }
  });

  // the edit query (["Contributions","edit",…]) to clear once ContributeTpl unmounts.
  const editQueryToClearRef = useRef(null);

  // runs AFTER the commit that unmounts ContributeTpl (surveyData === sentinel renders
  // like null), so removeQueries can't trigger a getContentForEdit refetch — there's no
  // observer left. Then reset surveyData to null (back to the list).
  useEffect(() => {
    if (surveyData !== UNMOUNT_CONTRIBUTE_TPL) return;
    const c = editQueryToClearRef.current;
    if (c) {
      queryClient.removeQueries(
        ["Contributions", "edit", c.contentType, c.documentId],
        { exact: true }
      );
      editQueryToClearRef.current = null;
    }
    setSurveyData(null);
  }, [surveyData]); // eslint-disable-line react-hooks/exhaustive-deps

  useQuickLinkParser({
    // orignalFrom is just pathname+query. so need to add this dummy url, otherwise the new Url class initiation will fail inside
    originalUrl: originalFrom.current
      ? "http://somedummyurlthatdoesntmatter.com" + originalFrom.current
      : originalFrom.current,
    userData,
    generateFn,
    generateTplIsLoading,
    setSurveyData,
  });

  const getActiveSurveys = () => {
    const configuredSurveys =
      profileConfig?.general?.selfServeSurveyConfig?.surveys || [];
    const userSurveys = userData?.user?.selfServeSurveys || [];

    return configuredSurveys.filter((configSurvey) => {
      // multi-submission surveys are standing "submit whenever" forms — they don't
      // track docs on user.selfServeSurveys, so there's nothing to gate on. always
      // show them once configured (initActive / sequencing is a single-survey concept).
      if (configSurvey.allowMultipleSubmissions) {
        return true;
      }
      if (userSurveys.length === 0) {
        return configSurvey.initActive;
      }
      const userSurvey = userSurveys.find(
        (us) => us.contentType === configSurvey.contentType
      );
      return !!userSurvey;
    });
  };

  const getCardData = (survey) => {
    const userSurvey = userData?.user?.selfServeSurveys?.find(
      (us) => us.contentType === survey.contentType
    );

    return {
      contentType: survey.contentType,
      status: userSurvey?.status || "Not Started",
      title:
        __GetContentTypeConfigNew(survey.contentType)?.general?.content
          ?.title || survey.contentType,
      description: userSurvey?.status === 'published'
        ? ''
        : userSurvey
          ? "Click to continue your survey"
          : "Click to start the survey",
    };
  };

  const cardConfig = {
    title: "title",
    cardLabel: "Survey",
    tagDisplay: "status",
    desc: "description",
  };

  const { _Nav, deployment } = useGetQueryData("platformConfigs");

  // shared "after publish/delete succeeds" handler for the self-serve flow: the
  // NATIVE publish/delete machinery runs as usual (banner, deletion, clone merge),
  // it just doesn't navigate — we stay on /self-serve-survey, refresh the list,
  // and drop back to it. Used by both selfServePublish and selfServeActions.
  const onAfterSuccess = () => {
    // remember which edit query to clear, then unmount ContributeTpl via the sentinel.
    // The cleanup (removeQueries) happens in the effect above — i.e. AFTER the unmount
    // commits — so it can't trigger a getContentForEdit refetch / spurious clone.
    editQueryToClearRef.current = surveyData
      ? { contentType: surveyData.contentType, documentId: surveyData.documentId }
      : null;
    setSurveyData(UNMOUNT_CONTRIBUTE_TPL);
    queryClient.invalidateQueries("contentListing");
    queryClient.invalidateQueries("userData");
  };

  // single-submission click: open the user's one tracked doc, or create it.
  const handleSurveyClick = (e, data) => {
    const userSurvey = userData?.user?.selfServeSurveys?.find(
      (us) => us.contentType === data.contentType && !!us.documentId === true
    );

    if (userSurvey) {
      setSurveyData({
        contentType: data.contentType,
        documentId: userSurvey.documentId,
        mode: userSurvey.status === "published" ? "view" : "edit",
      });
    } else {
      if (!generateTplIsLoading) {
        generateFn(
          {
            kp_content_type: data.contentType,
            kp_settings: [],
          },
          {
            onSuccess: async (res) => {
              await queryClient.invalidateQueries("userData");

              setSurveyData({
                contentType: data.contentType,
                documentId: res.data.content._id,
                mode: "edit",
              });
            },
            updateSelfServeSurvey: true,
          }
        );
      }
    }
  };

  // multi-submission: open an existing doc from the listing (by status).
  const handleOpenSubmission = (doc) => {
    if (doc.kp_published_status === "published") {
      setSurveyData({
        contentType: doc.contentType,
        documentId: doc._id,
        mode: "view",
      });
      return;
    }
    // editPublished is the in-progress edit clone of a published doc. To resume
    // editing, getContentForEdit needs the OG published doc id (stored on the
    // clone as kp_og_published_doc) — NOT the clone's own _id.
    const editId =
      doc.kp_published_status === "editPublished"
        ? doc.kp_og_published_doc
        : doc._id;
    setSurveyData({
      contentType: doc.contentType,
      documentId: editId,
      mode: "edit",
    });
  };

  // multi-submission: start a fresh draft. NOTE: no `updateSelfServeSurvey` flag,
  // so this skips the user-doc array writes and the 1-per-content-type guard.
  const handleNewSubmission = (contentType) => {
    if (generateTplIsLoading) return;
    generateFn(
      {
        kp_content_type: contentType,
        kp_settings: [],
      },
      {
        onSuccess: async (res) => {
          setSurveyData({
            contentType,
            documentId: res.data.content._id,
            mode: "edit",
          });
        },
      }
    );
  };

  const activeSurveys = getActiveSurveys();
  const singleSurveys = activeSurveys.filter((s) => !s.allowMultipleSubmissions);
  const multiSurveys = activeSurveys.filter((s) => s.allowMultipleSubmissions);

  // tab list: one shared "Surveys" tab for all single-submission surveys (if any),
  // then one dedicated tab per multi-submission content type.
  const tabs = [
    ...(singleSurveys.length > 0
      ? [{ value: SURVEYS_TAB, display: "Surveys" }]
      : []),
    ...multiSurveys.map((s) => ({
      value: s.contentType,
      display:
        __GetContentTypeConfigNew(s.contentType)?.general?.content?.title ||
        s.contentType,
    })),
  ];

  const [activeTab, setActiveTab] = useState(tabs[0]?.value);
  const currentTab = tabs.find((t) => t.value === activeTab)
    ? activeTab
    : tabs[0]?.value;

  // options for the header "New Submission" dropdown — one per multi-submission
  // content type. Only rendered when at least one such content type is configured.
  const newSubmissionOptions = multiSurveys.map((s) => ({
    value: s.contentType,
    display:
      __GetContentTypeConfigNew(s.contentType)?.general?.content?.title ||
      s.contentType,
  }));

  // the sentinel renders like `null` (falls through to the list) so ContributeTpl /
  // SM_PublishedPage unmount; the cleanup effect then clears the edit query.
  if (surveyData && surveyData !== UNMOUNT_CONTRIBUTE_TPL) {
    // is the doc being edited part of a multi-submission content type?
    const isMultiEditor = !!activeSurveys.find(
      (s) =>
        s.contentType === surveyData.contentType && s.allowMultipleSubmissions
    );

    // Reuse the published page's OWN edit/delete buttons + access gating (TplActions
    // decides whether to show them, based on the participant's UPDATE/DELETE access +
    // authorship). We only override their navigation: edit flips to inline edit mode;
    // delete runs the native DeleteModal/confirm but returns to the list (onAfterSuccess).
    const selfServeActions = {
      onEdit: () => setSurveyData({ ...surveyData, mode: "edit" }),
      onAfterSuccess,
    };

    return surveyData.mode === "view" ? (
      <div>
        <SM_PublishedPage
          disableBreadcrumbsOnV2
          contentType={surveyData.contentType}
          contentId={surveyData.documentId}
          selfServeActions={selfServeActions}
          onBack={() => setSurveyData(null)}
        />
      </div>
    ) : (
      <Fragment>
        <ContributeTpl
          onboardingProps={{
            edit_moderate: "edit",
            contentType: surveyData.contentType,
            contributionId: surveyData.documentId,
          }}
          // reuse ContributeTpl's NATIVE publish; just keep the participant on
          // /self-serve-survey and tag the survey status for single-submission flows.
          selfServePublish={{
            updateSelfServeSurvey: !isMultiEditor,
            onAfterSuccess,
          }}
        />
      </Fragment>
    );
  }

  const renderSurveysList = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      {singleSurveys.map((survey) => (
        <Fragment key={survey.contentType}>
          <CardContent
            id={`survey-${survey.contentType}`}
            data={getCardData(survey)}
            config={cardConfig}
            onClick={handleSurveyClick}
            statusTagVariant={
              survey.status === "published" ? "positive" : "neutral"
            }
          />
        </Fragment>
      ))}
    </div>
  );

  const renderTabContent = (tabValue) => {
    if (tabValue === SURVEYS_TAB) return renderSurveysList();
    return (
      <MultiSubmissionTab
        contentType={tabValue}
        userId={userData?.user?._id}
        onOpenSubmission={handleOpenSubmission}
      />
    );
  };

  return (
    <div>
      {generateTplIsLoading && (
        <LoaderOverlay loaderText="Loading survey form..." />
      )}
      <Section borderBottom style={{ zIndex: 10, position: "relative" }}>
        <Container>
          <div
            style={{
              height: "6rem",
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <HeaderLogo config={{ _Nav, deployment }} />
              <UI_TITLE_SM>Data Submission</UI_TITLE_SM>
            </div>
            <ButtonGhostV2
              icon="Power"
              value="Logout"
              onClick={logout}
            />
          </div>
        </Container>
      </Section>
      <Section>
        <div
          style={{ background: colors2.grey5, height: "calc(100dvh - 6rem)" }}
        >
          <Wrapper700 style={{ height: "100%" }}>
            <div
              style={{
                background: "white",
                height: "100%",
                // scroll the submissions within the fixed-height card instead of
                // overflowing the page; also gives the lazy-load observer its scroll root.
                overflowY: "auto",
                boxShadow: "2px 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              <Container>
                <PaddingTop40 />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "1rem",
                    borderBottom: `1px solid ${colors2.grey10}`,
                    paddingBottom: "1rem",
                    marginBottom: "2rem",
                  }}
                >
                  <UI_BODY_SEMIBOLD_SM>My Submissions</UI_BODY_SEMIBOLD_SM>
                  {/* single "New Submission" action — only when at least one
                      content type allows multiple submissions. Opens a dropdown
                      of those content types; selecting one starts a fresh draft. */}
                  {newSubmissionOptions.length > 0 && (
                    <DropdownSingle
                      id="selfServeNewSubmission"
                      options={newSubmissionOptions}
                      value={undefined}
                      saveValueAsString
                      onChange={(id, ct) => handleNewSubmission(ct)}
                      genCustomSelectComp={() => (
                        <ButtonPrimaryCompact
                          icon="Plus"
                          value="New Submission"
                          disabled={generateTplIsLoading}
                        />
                      )}
                    />
                  )}
                </div>
                {/* tab bar only renders when there's more than one tab; a pure
                    single-submission tenant looks exactly like before. */}
                {tabs.length > 1 && (
                  <div style={{ marginBottom: "2rem" }}>
                    <TabBarV2
                      id="selfServeTabs"
                      options={tabs}
                      value={currentTab}
                      onChange={(id, v) => setActiveTab(v)}
                      saveValueAsString
                    />
                  </div>
                )}
                {tabs.length > 0 && renderTabContent(currentTab)}
                <PaddingBottom20 />
              </Container>
            </div>
          </Wrapper700>
        </div>
      </Section>

      {tabs.length === 0 && (
        <div className="text-center text-gray-500 mt-8">
          No active surveys available
        </div>
      )}

      {/* {originalFrom.current && (
        <div className="mt-4 text-sm text-gray-500">
          Original URL: {originalFrom.current}
        </div>
      )} */}
    </div>
  );
};

export default SelfServeSurvey;
